import { BehaviorSubject, Subject, distinctUntilChanged, filter, map, race, switchMap, take, takeLast, tap, throwError, timer } from 'rxjs';
import { getClassName } from '../../class-name';
export var ActionQueueBehavior = /*#__PURE__*/function (ActionQueueBehavior) {
  ActionQueueBehavior["blockIfExecuting"] = "blockIfExecuting";
  ActionQueueBehavior["abortPrevious"] = "abortPrevious";
  ActionQueueBehavior["enqueue"] = "enqueue";
  return ActionQueueBehavior;
}(ActionQueueBehavior || {});
export var ActionQueueHandlerCompleteOn = /*#__PURE__*/function (ActionQueueHandlerCompleteOn) {
  ActionQueueHandlerCompleteOn["completedObservable"] = "complete";
  ActionQueueHandlerCompleteOn["take1"] = "take1";
  return ActionQueueHandlerCompleteOn;
}(ActionQueueHandlerCompleteOn || {});
const actionQueueDefaultConfig = {
  behavior: ActionQueueBehavior.enqueue,
  handlerCompleteOn: ActionQueueHandlerCompleteOn.completedObservable,
  timeout: 2000
};
/**
 * actionQueue aims to provide action/dispatch mechanisms as known from ngxs or redux.
 * dispatchable actions are instances of a simple class containing a payload.
 * ActionQueue provides multiple mechanisms on how to handle execution of multiple actions:
 * - enqueue incoming actions to run in order,
 * - reject them until current action finishes,
 * - abort current and execute incoming action asap.
 *
 * TODO: Allow actions to specify own behavior - allow/deny abortion
 * TODO: Allow to set inidividual queue behavior for incoming action - enqueue/override/block
 */
class ActionQueueError extends Error {}
class ActionQueueAbortError extends ActionQueueError {}
class ActionQueueTimeoutError extends ActionQueueError {}
export class ActionQueue {
  get currentAction$() {
    return this.executingAction$;
  }
  constructor(config) {
    this.destroyed$ = new Subject();
    this.abortCurrentAction$ = new Subject();
    this.dispatchAction$ = new Subject();
    this.executingAction$ = new BehaviorSubject(null);
    this.handlers = new Map();
    this.queue$ = new BehaviorSubject([]);
    this.onActionRejected$ = new Subject();
    this.onActionAborted$ = new Subject();
    this.onActionCompleted$ = new Subject();
    this.config = {
      ...actionQueueDefaultConfig,
      ...config
    };
    this.setupQueueExecution();
    // this.queue$.subscribe(q=>console.log('>>>',q))
  }
  setupQueueExecution() {
    const onNextAction$ = this.queue$.pipe(map(queue => queue.length ? queue[0] : null), distinctUntilChanged(),
    // required as enqueueing additional actions re-emits the same action at [0]!
    tap(action => this.executingAction$.next(action)),
    // manage emission of executingAction
    filter(action => action !== null) // dont emit if there is no action
    );
    onNextAction$.subscribe(action => {
      const handler = this.getHandler(action);
      if (!handler) return;
      race(this.prepareHandlerExecution(handler, action), this.abortCurrentAction$.pipe(switchMap(() => {
        return throwError(() => new ActionQueueAbortError());
      })), timer(this.config.timeout).pipe(switchMap(() => {
        if (!IS_PRODUCTION) console.warn('ActionQueue reached timeout before action was completed: ', action);
        return throwError(() => new ActionQueueTimeoutError());
      }))).subscribe({
        next: result => this.onExecutionComplete(result, handler, action),
        error: err => this.onExecutionError(err, handler, action)
      });
    });
  }
  dispatch(action) {
    this.handleIncomingAction(action, this.executingAction$.getValue());
  }
  registerHandler(action, handler) {
    this.handlers.set(action, handler);
  }
  destroy() {
    this.destroyed$.next(void 0);
  }
  handleIncomingAction(action, executingAction) {
    const behavior = this.config.behavior;
    if (behavior === ActionQueueBehavior.abortPrevious) {
      if (executingAction) {
        // cancel current queue and when it is empty, enqueue the incoming action
        this.abortCurrentAction$.next(0);
        this.executingAction$.pipe(filter(a => a === null), take(1)).subscribe(() => {
          this.addToQueue(action);
        });
      } else {
        this.addToQueue(action);
      }
    } else if (behavior === ActionQueueBehavior.blockIfExecuting) {
      if (executingAction) {
        this.onActionRejected$.next({
          action,
          message: 'Conflict: other action is still executing'
        });
        return;
      } else {
        this.addToQueue(action);
      }
    } else {
      this.addToQueue(action);
    }
  }
  // ========  QUEUE HANDLING  ==================================================
  addToQueue(action) {
    const newQueue = [...this.queue$.getValue(), action];
    this.queue$.next(newQueue);
  }
  removeFromQueue(action) {
    const queue = this.queue$.getValue();
    if (queue[0] !== action) throw new Error('currently only linear processing of queue is supported. trying to remove an action from queue which is not first in list is unsupported.');
    this.queue$.next(queue.slice(1));
  }
  // ========  ACTION HANDLER FUNCTIONALITY  ==================================================
  getHandler(action) {
    const actionCtor = action.constructor;
    if (!this.handlers.has(actionCtor)) {
      if (!IS_PRODUCTION) console.warn('ActionQueue has no registered handler for this action ' + actionCtor.name);
      this.onActionRejected$.next({
        action,
        message: 'ActionQueue has no registered handler for this action'
      });
      return false;
    }
    return this.handlers.get(actionCtor);
  }
  prepareHandlerExecution(handler, action) {
    const handlerCompletionMode = this.config.handlerCompleteOn || ActionQueueHandlerCompleteOn.completedObservable;
    return handler(action, this.abortCurrentAction$).pipe(handlerCompletionMode === ActionQueueHandlerCompleteOn.completedObservable ? take(1) // wait for first value, ignore everything after
    : takeLast(1) // wait for completion, emit last value
    );
  }
  onExecutionComplete(result, handler, action) {
    this.onActionCompleted$.next({
      action,
      result
    });
    this.removeFromQueue(action);
  }
  onExecutionError(err, handler, action) {
    if (err instanceof ActionQueueError) {
      if (err instanceof ActionQueueAbortError) {
        this.onActionAborted$.next({
          action,
          info: 'aborted'
        });
      } else if (err instanceof ActionQueueTimeoutError) {
        this.onActionAborted$.next({
          action,
          info: 'timeout'
        });
      }
    } else {
      // handler itself has generated an error
      if (!IS_PRODUCTION) {
        console.error('ActionQueue error: handler of ' + getClassName(action) + ' failed: ' + err);
        console.error(err);
      }
      this.onActionRejected$.next({
        action,
        message: err
      });
    }
    this.removeFromQueue(action);
  }
}